/*
  HN PDP Cart Optimistic
  Shared client-side optimistic-update layer for every ATC path
  on the product page:
    - Main product form (single-variant + multi-variant, Dawn
      <product-form> handles the actual AJAX; we layer optimistic
      open + ghost insert on top).
    - Sticky bottom-bar CTA (submits to the same main product
      form via the form="..." attribute, so it benefits from
      the main form's handler automatically).
    - Bundle ATC (custom multi-line-item add handled in
      hn-pdp-main.js bundleAddToCart). Bundles call
      HnPdpCart.openDrawerOptimistic() directly; ghost insertion
      is skipped because building credible ghost rows for N
      bundle items with shared bundle_id properties is
      disproportionate work for the marginal perceived win.
    - Final CTA (its own AJAX in hn-pdp-cta.liquid). Uses its
      own helpers; eventual cleanup may consolidate here.

  Strategy:
    1. On submit / click: open the drawer immediately (animation
       starts on the same frame as the click).
    2. Insert a ghost <tr.cart-item> at the TOP of the drawer's
       items table (cart-drawer renders newest first) — built
       from product data stashed in a JSON <script> block by
       hn-pdp-main.liquid. The ghost pulses subtly via CSS to
       read as "in flight".
    3. Existing handlers (Dawn product-form, bundleAddToCart)
       fire their AJAX. When their cartUpdate event lands or
       they swap section HTML into #CartDrawer, our ghost is
       replaced with the canonical row. Section swap is what
       reconciles count badge, shipping bar, subtotals.
    4. On error: roll back the ghost row + empty-state mutation
       so the drawer goes back to its pre-click state.

  Loaded EARLY (no defer) on product templates so the listener
  is bound before the user can plausibly click ATC. Exposes
  helpers on window.HnPdpCart for other scripts to compose with.
*/

(function () {
  'use strict';
  if (window.HnPdpCart && window.HnPdpCart.__bound) return;

  /* ---------- Helpers ---------- */

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoneyCents(cents, format) {
    if (
      typeof window.Shopify !== 'undefined' &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      try {
        return window.Shopify.formatMoney(cents, format || '${{amount}}');
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + (parseInt(cents, 10) / 100).toFixed(2);
  }

  function readProductData(context) {
    var root = null;
    if (context && context.closest) {
      root = context.closest('product-form, [data-hn-card], [data-hn-pdp]');
    }

    var el = root && root.querySelector
      ? root.querySelector('[data-hn-pdp-product-data], [data-hn-optimistic-product-data]')
      : null;
    if (!el && context && context.closest) {
      var modal = context.closest('quick-add-modal');
      el = modal && modal.querySelector
        ? modal.querySelector('[data-hn-pdp-product-data], [data-hn-optimistic-product-data]')
        : null;
    }
    if (!el) {
      el = document.querySelector('[data-hn-pdp-product-data]');
    }
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* Resolve the active variant for the given form. Reads the
     hidden `id` input (always current after Shopify's variant
     swap script runs) and looks up the matching variant in the
     product data block. Returns null if anything's missing. */
  function resolveVariant(form, productData) {
    if (!form || !productData || !productData.variants) return null;
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput) return null;
    var variantId = parseInt(idInput.value, 10);
    if (!variantId) return null;
    for (var i = 0; i < productData.variants.length; i++) {
      if (productData.variants[i].id === variantId) {
        return productData.variants[i];
      }
    }
    return null;
  }

  /* ---------- Optimistic drawer open ---------- */

  function openDrawerOptimistic(triggerEl) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    if (window.HnAnalytics && typeof window.HnAnalytics.trackCartDrawerView === 'function') {
      window.HnAnalytics.trackCartDrawerView({ triggeredBy: triggerEl || null });
    }
    drawer.classList.add('is-loading');
    if (typeof drawer.open === 'function') {
      drawer.open(triggerEl || null);
    } else {
      drawer.classList.add('active', 'is-open');
      drawer.removeAttribute('hidden');
    }
    /* Lock checkout button(s) while the add is in flight.
       Without this, on slow networks the customer can click
       ATC then click Checkout faster than the add completes,
       paying for a cart without the item they just added.
       Re-enables on clearDrawerLoading / safety timeout /
       cartError, and the post-success section swap brings
       in a fresh (enabled) button anyway. */
    disableCheckoutButtons();
    /* Arm safety net — if no event fires in 4s, force-clear
       loading so the bar can't get stuck. */
    if (typeof armSafetyTimeout === 'function') armSafetyTimeout();
    return drawer;
  }

  function clearDrawerLoading() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.remove('is-loading');
    enableCheckoutButtons();
    if (typeof clearSafetyTimeout === 'function') clearSafetyTimeout();
  }

  /* ---------- Checkout button lock ---------- */

  function findCheckoutButtons() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return [];
    /* Multiple selectors defensively cover Dawn defaults +
       common custom patterns. */
    return Array.prototype.slice.call(drawer.querySelectorAll(
      '#CartDrawer-Checkout, button[name="checkout"], .cart__checkout-button, a[href*="/checkout"]'
    ));
  }

  function disableCheckoutButtons() {
    var btns = findCheckoutButtons();
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      /* Skip buttons already disabled by something else (Dawn
         disables on sold-out, etc.) — re-enabling those later
         would be wrong. */
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') continue;
      btn.setAttribute('data-hn-pdp-locked-checkout', '1');
      /* For real <button> elements: `disabled` blocks click
         submission entirely (browsers don't fire submit on a
         disabled button). For <a> elements: aria-disabled is
         the best we can do without breaking nav semantics —
         layer with pointer-events: none via inline style. */
      if (btn.tagName === 'BUTTON' || btn.tagName === 'INPUT') {
        btn.disabled = true;
      } else {
        btn.style.pointerEvents = 'none';
      }
      btn.setAttribute('aria-disabled', 'true');
    }
  }

  function enableCheckoutButtons() {
    /* Only re-enable buttons WE locked, not buttons that were
       already disabled before the add (out-of-stock states,
       etc.). The data-attr is our marker. */
    var btns = document.querySelectorAll(
      'cart-drawer [data-hn-pdp-locked-checkout]'
    );
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (btn.tagName === 'BUTTON' || btn.tagName === 'INPUT') {
        btn.disabled = false;
      } else {
        btn.style.pointerEvents = '';
      }
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('data-hn-pdp-locked-checkout');
    }
  }

  /* ---------- Ghost row insertion ---------- */

  /* Walks the DOM for active cart-level discount chips marked
     by hn-cart-order-discounts.liquid with the
     data-hn-cart-level-discount attribute, dedupes by chip text,
     and returns an HTML string ready to inject into the ghost
     row's .discounts ul. Cart-level codes apply identically to
     every line item, so any existing row is a valid source. */
  function snapshotCartLevelDiscountsHTML() {
    var seen = {};
    var out = '';
    document.querySelectorAll('[data-hn-cart-level-discount]').forEach(function (li) {
      var key = (li.textContent || '').replace(/\s+/g, ' ').trim();
      if (!key || seen[key]) return;
      seen[key] = true;
      out += li.outerHTML;
    });
    return out;
  }

  function buildGhostRowHTML(ctx) {
    var imgHTML = '';
    if (ctx.image) {
      imgHTML =
        '<a href="' + escapeHtml(ctx.url) + '" class="cart-item__link" tabindex="-1" aria-hidden="true"></a>' +
        '<img class="cart-item__image" src="' + escapeHtml(ctx.image) +
        '" alt="' + escapeHtml(ctx.image_alt) +
        '" loading="lazy" width="150" height="' + escapeHtml(ctx.image_height || 150) + '">';
    }

    var qty = parseInt(ctx.quantity, 10) || 1;
    var priceCents = parseInt(ctx.price, 10) || 0;
    var compareAt = parseInt(ctx.compare_at_price, 10) || 0;
    /* A confirmed cart-level discount (for example, 25% off) discounts the
       line price exactly the way the canonical row will, so apply it
       here too — otherwise the row flashes full price on the click
       frame then "corrects" down when the server swap lands. */
    var orderPct = readActiveOrderPct();
    /* The SALE chip reflects the compare-at markdown only; the order
       code shows as its own cloned cart-level chip below. */
    var showSaleChip = compareAt > priceCents && compareAt > 0;
    var salePct = showSaleChip ? Math.round(((compareAt - priceCents) / compareAt) * 100) : 0;
    /* Strike = retail (MAX(compare_at, price) x qty). Final = price
       after the order code, floored like checkout. Show the strike
       whenever a markdown OR a code makes the two differ. */
    var retailLine = (showSaleChip ? compareAt : priceCents) * qty;
    var lineCents = lineFinalAfterOrder(priceCents * qty, orderPct);
    var strikeLine = retailLine;
    var showStrike = retailLine > lineCents;

    /* Discount chips on the ghost row.
       1. Synthetic SALE chip when there's a retail markdown
          (compare_at > price). Captured from the upsell ctx.
       2. Confirmed cart-level discounts cloned from any
          existing cart row so the ghost matches what the
          canonical row will show right away — no need to wait
          for the section swap. */
    var saleHTML = '';
    if (showSaleChip) {
      saleHTML =
        '<li class="discounts__discount">' +
          '<svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12">' +
            '<path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor">' +
          '</svg>' +
          ' SALE (' + salePct + '% OFF)' +
        '</li>';
    }
    var cartLevelHTML = snapshotCartLevelDiscountsHTML();
    var discountsHTML = '';
    if (saleHTML || cartLevelHTML) {
      discountsHTML =
        '<ul class="discounts list-unstyled" role="list" aria-label="Discount">' +
          saleHTML + cartLevelHTML +
        '</ul>';
    }

    var showVariant = ctx.variant_title && ctx.variant_title !== 'Default Title' && !ctx.has_only_default_variant;
    var variantLabel = showVariant ? ctx.variant_title : (qty === 1 ? 'Item' : 'Items');

    var chipBlock =
      '<div class="hn-bundle-chips" role="list">' +
        '<div class="hn-bundle-chip" role="listitem">' +
          '<button type="button" class="hn-bundle-chip__face hn-bundle-chip__face--collapsed" disabled aria-label="Adding to cart">' +
            '<span class="hn-bundle-chip__qty">' + qty + '&times;</span>' +
            '<span class="hn-bundle-chip__name">' + escapeHtml(variantLabel) + '</span>' +
          '</button>' +
        '</div>' +
      '</div>';

    var priceBlock;
    if (showStrike) {
      priceBlock =
        '<div class="cart-item__price-wrapper">' +
          '<div class="cart-item__discounted-prices">' +
            '<s class="cart-item__old-price price price--end">' +
              formatMoneyCents(strikeLine, ctx.money_format) +
            '</s>' +
            '<span class="price price--end">' +
              formatMoneyCents(lineCents, ctx.money_format) +
            '</span>' +
          '</div>' +
        '</div>';
    } else {
      priceBlock =
        '<div class="cart-item__price-wrapper">' +
          '<span class="price price--end">' +
            formatMoneyCents(lineCents, ctx.money_format) +
          '</span>' +
        '</div>';
    }

    /* data-hn-variant-id lets subsequent adds of the same
       variant find this row and bump its quantity instead of
       stacking a duplicate ghost. */
    var vidAttr = ctx.variant_id
      ? ' data-hn-variant-id="' + escapeHtml(ctx.variant_id) + '"'
      : '';

    return (
      '<tr class="cart-item" data-hn-pdp-ghost' + vidAttr + ' role="row">' +
        '<td class="cart-item__media" role="cell">' + imgHTML + '</td>' +
        '<td class="cart-item__details" role="cell">' +
          discountsHTML +
          '<a href="' + escapeHtml(ctx.url) + '" class="cart-item__name h4 break">' +
            escapeHtml(ctx.title) +
          '</a>' +
          chipBlock +
        '</td>' +
        '<td class="cart-item__totals right" role="cell">' +
          priceBlock +
        '</td>' +
      '</tr>'
    );
  }

  /* Find an existing row in the drawer for the given variant.
     Checks ghost rows first (most recent state), then canonical
     server-rendered rows. Returns null if no match. */
  function findExistingRow(drawer, variantId) {
    if (!drawer || !variantId) return null;
    var ghost = drawer.querySelector(
      'tr[data-hn-pdp-ghost][data-hn-variant-id="' + variantId + '"]'
    );
    if (ghost) return { row: ghost, type: 'ghost' };

    /* HN cart rows expose the variant id on the row itself via
       data-hn-variant-id. Covers both non-bundle <tr class="cart-
       item"> and single-variant bundle <tr class="cart-item--
       bundle-member">. Cheaper than the Dawn fallback below and
       matches first since most cart rows in the drawer carry this
       attr. */
    var hnRow = drawer.querySelector(
      '[data-hn-variant-id="' + variantId + '"]:not([data-hn-pdp-ghost])'
    );
    if (hnRow) return { row: hnRow, type: 'hn' };

    /* Dawn-style fallback: canonical rows expose the variant id
       on the quantity input via data-quantity-variant-id. Still
       supported for any non-HN row markup that might render. */
    var qtyInput = drawer.querySelector(
      'input[data-quantity-variant-id="' + variantId + '"]'
    );
    if (qtyInput) {
      var row = qtyInput.closest('tr.cart-item');
      if (row) return { row: row, type: 'canonical' };
    }
    return null;
  }

  /* Bump an existing row's quantity + line price. Captures old
     values so revertGhostRow can restore them on error.
     Handles three row flavors:
       'hn'        — HN cart-drawer/cart-page rows. Qty lives on
                     data-hn-variant-qty + a chip text node; price
                     lives in a .price selector (drawer) or
                     .hn-cart-page__item-price-final (cart page).
       'canonical' — Dawn rows. Qty on input.quantity__input.
       'ghost'     — Our own ghost rows (PDP-style). Same DOM as
                     canonical so the Dawn path handles them. */
  function bumpExistingRow(existing, deltaQty, unitPriceCents) {
    var row = existing.row;
    var type = existing.type;

    if (type === 'hn') {
      return bumpHnRow(row, deltaQty, unitPriceCents);
    }

    var qtyInput = row.querySelector('input.quantity__input, input[type="number"]');
    var priceEl = row.querySelector('.cart-item__price-wrapper .price--end') ||
                  row.querySelector('.cart-item__price-wrapper .price:last-child');

    var oldQty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    var newQty = oldQty + deltaQty;
    var oldPriceText = priceEl ? priceEl.textContent : null;

    if (qtyInput) {
      qtyInput.value = String(newQty);
      qtyInput.setAttribute('value', String(newQty));
    }
    /* Best-effort price update. Doesn't account for per-line
       discounts (bundles get their own bundle_id so wouldn't
       hit this same-variant matcher anyway), but for the typical
       single-variant case unitPrice * newQty is correct. Server
       reconciles within ~500ms either way. */
    if (priceEl && unitPriceCents) {
      priceEl.textContent = formatMoneyCents(unitPriceCents * newQty);
    }

    return {
      row: row,
      revert: {
        type: 'bumped',
        oldQty: oldQty,
        oldPriceText: oldPriceText,
        qtyInput: qtyInput,
        priceEl: priceEl
      }
    };
  }

  /* Parses window.__hnBundleTiers (set in theme.liquid via
     hn-bundle-tiers.liquid) once and caches the result. Returns
     { qtys: [Number], pcts: [Number] } or null if no ladder is
     configured. */
  var _hnBundleTiersCache = null;
  function getBundleTiers() {
    if (_hnBundleTiersCache !== null) return _hnBundleTiersCache;
    _hnBundleTiersCache = false; // sentinel for "tried but absent"
    var raw = window.__hnBundleTiers;
    if (typeof raw !== 'string' || !raw) return null;
    var parts = raw.split('|');
    if (parts.length < 2 || !parts[0] || !parts[1]) return null;
    var qtys = parts[0].split(',').map(function (s) { return parseInt(s, 10); }).filter(Boolean);
    var pcts = parts[1].split(',').map(function (s) { return parseInt(s, 10); }).filter(function (n) { return !isNaN(n); });
    if (!qtys.length || qtys.length !== pcts.length) return null;
    _hnBundleTiersCache = { qtys: qtys, pcts: pcts };
    return _hnBundleTiersCache;
  }

  /* Look up the bundle tier % for a given total qty. Returns 0
     when the qty is below the lowest tier (or no ladder). */
  function bundleTierPctFor(qty) {
    var t = getBundleTiers();
    if (!t) return 0;
    var pct = 0;
    for (var i = 0; i < t.qtys.length; i++) {
      if (qty >= t.qtys[i]) pct = t.pcts[i];
    }
    return pct;
  }

  /* Whether a cart row's PRODUCT is bundle-eligible. The tier ladder
     applies by quantity alone, so re-adding a NON-eligible product
     (e.g. a toiletry bag) via an upsell bump must NOT apply a tier % or
     inject a phantom "X Pack (Y% OFF)" chip. Mirrors isRowBundleEligible
     in hn-cart-drawer.js (separate IIFE, can't share). Signals:
     data-hn-bundle-eligible (regular eligible rows, set in Liquid),
     data-hn-bundle-product-id (combined bundle row), .cart-item--bundle-member. */
  function isRowBundleEligible(row) {
    if (!row) return false;
    return (
      row.getAttribute('data-hn-bundle-eligible') === 'true' ||
      row.hasAttribute('data-hn-bundle-product-id') ||
      (row.classList && row.classList.contains('cart-item--bundle-member'))
    );
  }

  /* HN-row bump.
     Walks the row's DOM to find:
       - data-hn-variant-qty attr (the row-level qty count)
       - .hn-bundle-chip__qty / .hn-bundle-chip__value
         (the visible "N×" count on the chip stepper)
       - .price.price--end or .hn-cart-page__item-price-final
         (the line price text)
       - .discounts ul (existing discount chips on the row)
     Then updates all of them, applying the new bundle tier %
     so the line price reflects the tier discount AND a synthetic
     "N PACK (X% OFF)" chip is injected/updated on the row's
     discounts ul. Captures old values for revert on error. */
  function bumpHnRow(row, deltaQty, unitPriceCents) {
    var oldQty = parseInt(row.getAttribute('data-hn-variant-qty'), 10) || 1;
    var newQty = oldQty + deltaQty;
    if (newQty < 1) newQty = 1;

    var chipQty = row.querySelector('.hn-bundle-chip__qty');
    var chipValue = row.querySelector('.hn-bundle-chip__value');
    /* Drawer line price (Dawn-style price wrapper kept on HN rows)
       and cart-page line price (hn-cart-page__item-price-final). */
    var priceEl =
      row.querySelector('.cart-item__price-wrapper .price--end') ||
      row.querySelector('.cart-item__price-wrapper .price:last-child') ||
      row.querySelector('.hn-cart-page__item-price-final');

    var oldChipQtyText = chipQty ? chipQty.textContent : null;
    var oldChipValueText = chipValue ? chipValue.textContent : null;
    var oldPriceText = priceEl ? priceEl.textContent : null;

    row.setAttribute('data-hn-variant-qty', String(newQty));
    if (chipQty) chipQty.textContent = newQty + '×'; /* × */
    if (chipValue) chipValue.textContent = String(newQty);

    /* Bundle tier math via the PROPORTIONAL method, so any
       cart-level percentage discount already
       baked into the old displayed price stays baked into the
       new one. Math:
         new = old × (newQty × (100 - newTier))
                   ÷ (oldQty × (100 - oldTier))
       The cart-level discount factor is identical in numerator
       and denominator and cancels — no need to know cartPct
       here. Falls back to the raw-unit calc only when the old
       text can't be parsed (e.g. first add into an empty row).
       Tier only applies to bundle-eligible products; a non-eligible
       row gets 0 on both sides (pure qty scaling) and skips the bundle
       chip block below, so no phantom "X Pack" chip flickers in. */
    var rowEligible = isRowBundleEligible(row);
    var oldTierPct = rowEligible ? bundleTierPctFor(oldQty) : 0;
    var newTierPct = rowEligible ? bundleTierPctFor(newQty) : 0;
    if (priceEl) {
      var cleanedOldPrice = oldPriceText
        ? String(oldPriceText).replace(/[^0-9.,-]/g, '').replace(/,/g, '')
        : '';
      var parsedOld = cleanedOldPrice ? parseFloat(cleanedOldPrice) : 0;
      var oldLineCents = parsedOld > 0 ? Math.round(parsedOld * 100) : 0;
      if (oldLineCents > 0) {
        var newLineCents = Math.round(
          oldLineCents * newQty * (100 - newTierPct) /
          (oldQty * (100 - oldTierPct))
        );
        priceEl.textContent = formatMoneyCents(newLineCents);
      } else if (unitPriceCents) {
        // Fallback: no parseable old text. Best-effort raw calc
        // (doesn't account for cart-level codes).
        var fallbackCents = Math.round(unitPriceCents * newQty * (100 - newTierPct) / 100);
        priceEl.textContent = formatMoneyCents(fallbackCents);
      }
    }

    /* Update or inject the synthetic bundle chip on the row's
       discounts ul so the discount labels match what the
       canonical row will show. Bundle chip text matches the
       Liquid: "{N} PACK ({X}% OFF)". Detection: a chip whose
       text starts with a digit + " PACK". Falls back to
       inserting a fresh chip when none exists yet. */
    var bundleChipReplaced = null;
    var bundleChipRemoved = null;
    var bundleChipInserted = null;
    if (newTierPct > 0) {
      /* Match the Liquid render in cart-drawer.liquid /
         main-cart-items.liquid: "{N} Pack ({X}% OFF)". The
         visual uppercase comes from CSS (.discounts__discount
         text-transform: uppercase) — keep the underlying text
         mixed-case so it matches the canonical chip when we
         look for it. */
      var bundleChipText = newQty + ' Pack (' + newTierPct + '% OFF)';
      var rowDiscountsUl = row.querySelector('ul.discounts');
      var existingBundleChip = null;
      if (rowDiscountsUl) {
        rowDiscountsUl.querySelectorAll('li.discounts__discount').forEach(function (li) {
          var txt = (li.textContent || '').trim();
          if (!existingBundleChip && /^\d+\s+PACK\b/i.test(txt)) {
            existingBundleChip = li;
          }
        });
      }
      if (existingBundleChip) {
        // Replace inner text after the icon-discount svg. Simpler:
        // capture old HTML for revert, replace last text node.
        bundleChipReplaced = { li: existingBundleChip, oldHTML: existingBundleChip.innerHTML };
        existingBundleChip.innerHTML = '<svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></svg> ' + bundleChipText;
      } else {
        var newLi = document.createElement('li');
        newLi.className = 'discounts__discount';
        newLi.innerHTML = '<svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></svg> ' + bundleChipText;
        if (rowDiscountsUl) {
          rowDiscountsUl.insertBefore(newLi, rowDiscountsUl.firstChild);
          bundleChipInserted = { li: newLi, ul: rowDiscountsUl };
        } else {
          // No discounts ul yet — create one on the details column
          // (cart-drawer .cart-item__details or cart-page
          // .hn-cart-page__item-details). Inject at the top.
          var detailsCol = row.querySelector('.cart-item__details, .hn-cart-page__item-details');
          if (detailsCol) {
            var ul = document.createElement('ul');
            ul.className = 'discounts list-unstyled';
            ul.setAttribute('role', 'list');
            ul.setAttribute('aria-label', 'Discount');
            ul.appendChild(newLi);
            detailsCol.insertBefore(ul, detailsCol.firstChild);
            bundleChipInserted = { li: newLi, ul: ul, createdUl: true };
          }
        }
      }
    }

    return {
      row: row,
      revert: {
        type: 'hn-bumped',
        oldQty: oldQty,
        oldChipQtyText: oldChipQtyText,
        oldChipValueText: oldChipValueText,
        oldPriceText: oldPriceText,
        chipQty: chipQty,
        chipValue: chipValue,
        priceEl: priceEl,
        bundleChipReplaced: bundleChipReplaced,
        bundleChipInserted: bundleChipInserted,
        bundleChipRemoved: bundleChipRemoved
      }
    };
  }

  function insertGhostRow(ctx) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    var contents = drawer.querySelector('#CartDrawer-CartItems');
    if (!contents) return null;

    /* If the same variant is already in the drawer (canonical
       row from a prior add, or a ghost from a rapid re-click),
       increment its quantity in place instead of stacking a
       duplicate ghost. Matches the post-server-swap behavior so
       the user doesn't see two identical rows briefly. */
    var variantId = parseInt(ctx.variant_id, 10);
    var qty = parseInt(ctx.quantity, 10) || 1;
    var unitPrice = parseInt(ctx.price, 10) || 0;
    if (variantId) {
      var existing = findExistingRow(drawer, variantId);
      if (existing) {
        return bumpExistingRow(existing, qty, unitPrice);
      }
    }

    /* Shared scaffolding (empty-state teardown + table creation +
       top-prepend + 'ghost' revert handle) lives in
       scaffoldAndPrependGhost so the bundle-ghost path reuses the
       exact same insertion + revert contract. */
    return scaffoldAndPrependGhost(buildGhostRowHTML(ctx));
  }

  /* Tear down the drawer's empty-state, ensure an items table
     exists, and prepend `html` as the TOP row. Returns
     { row, revert } with a 'ghost'-type revert handle (same shape
     the single-row path has always returned). Shared by the
     single-row and full bundle ghost builders. */
  function scaffoldAndPrependGhost(html) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    var contents = drawer.querySelector('#CartDrawer-CartItems');
    if (!contents) return null;

    var revertState = {
      type: 'ghost',
      drawer: drawer,
      emptyContent: null,
      createdWrapper: null,
      wasDrawerEmpty: drawer.classList.contains('is-empty'),
      cartItems: drawer.querySelector('cart-drawer-items'),
      cartItemsWasEmpty: false
    };

    var items = revertState.cartItems;
    if (items) {
      revertState.cartItemsWasEmpty = items.classList.contains('is-empty');
      items.classList.remove('is-empty');
    }
    drawer.classList.remove('is-empty');

    var emptyContent = drawer.querySelector('.drawer__inner-empty');
    if (emptyContent) {
      revertState.emptyContent = {
        node: emptyContent,
        parent: emptyContent.parentNode,
        nextSibling: emptyContent.nextSibling
      };
      emptyContent.parentNode.removeChild(emptyContent);
    }

    var tbody = contents.querySelector('.cart-items tbody');
    if (!tbody) {
      var wrapper = document.createElement('div');
      wrapper.className = 'drawer__cart-items-wrapper';
      wrapper.innerHTML =
        '<table class="cart-items" role="table">' +
          '<tbody role="rowgroup"></tbody>' +
        '</table>';
      contents.insertBefore(wrapper, contents.firstChild);
      revertState.createdWrapper = wrapper;
      tbody = wrapper.querySelector('tbody');
    }

    /* Prepend so the ghost lands at the TOP of the items list,
       matching where the server-rendered row will appear after
       the section swap. Cart drawer renders newest first; if we
       appended at the bottom the ghost would visually "jump" to
       the top on swap, which reads as a glitch. */
    tbody.insertAdjacentHTML('afterbegin', html);
    var ghost = tbody.querySelector('tr[data-hn-pdp-ghost]');

    return { row: ghost, revert: revertState };
  }

  function revertGhostRow(ghostRow, revertState) {
    if (!revertState) return;

    /* 'bumped' = we incremented an existing Dawn row's quantity
       instead of inserting a new ghost. Restore the original
       quantity + price text; do NOT remove the row. */
    if (revertState.type === 'bumped') {
      if (revertState.qtyInput) {
        revertState.qtyInput.value = String(revertState.oldQty);
        revertState.qtyInput.setAttribute('value', String(revertState.oldQty));
      }
      if (revertState.priceEl && revertState.oldPriceText != null) {
        revertState.priceEl.textContent = revertState.oldPriceText;
      }
      return;
    }

    /* 'hn-bumped' = we incremented an existing HN row's qty in
       place. Restore data-hn-variant-qty, chip text, line price,
       and any synthetic bundle chip we injected/replaced. The
       bumped row itself stays in place. */
    if (revertState.type === 'hn-bumped') {
      var hnRow = ghostRow; /* findExistingRow returned this row */
      if (hnRow && hnRow.setAttribute) {
        hnRow.setAttribute('data-hn-variant-qty', String(revertState.oldQty));
      }
      if (revertState.chipQty && revertState.oldChipQtyText != null) {
        revertState.chipQty.textContent = revertState.oldChipQtyText;
      }
      if (revertState.chipValue && revertState.oldChipValueText != null) {
        revertState.chipValue.textContent = revertState.oldChipValueText;
      }
      if (revertState.priceEl && revertState.oldPriceText != null) {
        revertState.priceEl.textContent = revertState.oldPriceText;
      }
      // Revert the bundle chip: either restore its prior text
      // (replaced case) or remove the chip + ul we created
      // (inserted case).
      if (revertState.bundleChipReplaced) {
        revertState.bundleChipReplaced.li.innerHTML = revertState.bundleChipReplaced.oldHTML;
      }
      if (revertState.bundleChipInserted) {
        var info = revertState.bundleChipInserted;
        if (info.createdUl && info.ul && info.ul.parentNode) {
          info.ul.parentNode.removeChild(info.ul);
        } else if (info.li && info.li.parentNode) {
          info.li.parentNode.removeChild(info.li);
        }
      }
      return;
    }

    /* 'ghost' = we inserted a new ghost row (and possibly built
       the items table from scratch for an empty cart). Remove
       everything we created and restore the empty-state markup. */
    if (ghostRow && ghostRow.parentNode) {
      ghostRow.parentNode.removeChild(ghostRow);
    }
    if (revertState.createdWrapper && revertState.createdWrapper.parentNode) {
      revertState.createdWrapper.parentNode.removeChild(revertState.createdWrapper);
    }
    if (revertState.emptyContent && revertState.emptyContent.parent) {
      revertState.emptyContent.parent.insertBefore(
        revertState.emptyContent.node,
        revertState.emptyContent.nextSibling
      );
    }
    if (revertState.cartItems && revertState.cartItemsWasEmpty) {
      revertState.cartItems.classList.add('is-empty');
    }
    if (revertState.drawer && revertState.wasDrawerEmpty) {
      revertState.drawer.classList.add('is-empty');
    }
  }

  /* ---------- Full-fidelity bundle ghost ----------
     A bundle ATC adds N line items at once and the drawer collapses
     them into a single combined card (>=2 distinct variants) or a
     single-variant member card (==1 variant, total qty >= the min
     tier). The bundle path used to skip the optimistic row entirely
     and wait for the server swap; this builds the real card client-
     side so the add feels instant, matching the single-product and
     upsell ATC paths. Mirrors snippets/cart-drawer.liquid:419-573
     (combined) and :628-772 (member). */

  /* Discount-chip icon, mirrors snippets/icon-discount.liquid so the
     ghost's synthetic chips match the canonical ones with no Liquid
     round-trip. */
  var DISCOUNT_ICON_SVG =
    '<svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor">' +
    '</svg>';

  /* Sum confirmed cart-level (order) discount percentages, deduped by
     chip text and capped at 95. Reads only rendered
     [data-hn-cart-level-discount] chips backed by Shopify cart
     allocations. It deliberately does not infer discounts from
     /discount URLs, cookies, or localStorage. */
  function readActiveOrderPct() {
    var seen = {};
    var total = 0;
    document.querySelectorAll('[data-hn-cart-level-discount]').forEach(function (li) {
      var txt = (li.textContent || '').replace(/\s+/g, ' ').trim();
      if (!txt || seen[txt]) return;
      seen[txt] = true;
      var m = txt.match(/(\d+(?:\.\d+)?)\s*%/);
      if (m) {
        var p = parseFloat(m[1]);
        if (!isNaN(p)) total += p;
      }
    });
    return total > 95 ? 95 : total;
  }

  /* Apply an order-level (cart code) percentage to a line subtotal.
     Shopify allocates an ORDER code per line by flooring the DISCOUNT
     amount (final = line − floor(line × code%)), NOT by flooring the
     price. Confirmed against a live cart: $89.99 at 25% renders $67.50
     (= 8999 − floor(2249.75)), not $67.49 (= floor(6749.25)). This
     differs from a bundle/PRODUCT discount, which floors the price —
     that step is the Math.floor in bundleLineFinalCents below, BEFORE
     this runs. Single source of truth for the order-discount step,
     shared by the single-row ghost price, the bundle line math, and the
     footer delta so they never disagree. */
  function lineFinalAfterOrder(rawLineCents, orderPct) {
    var pct = parseInt(orderPct, 10) || 0;
    if (pct <= 0) return rawLineCents;
    return rawLineCents - Math.floor(rawLineCents * pct / 100);
  }

  /* Final per-line price in cents: floor the bundle (product) discount
     on the line first (Shopify Function discount, floors the PRICE),
     then apply the order code (floors the DISCOUNT — see
     lineFinalAfterOrder). */
  function bundleLineFinalCents(unitPriceCents, qty, tierPct, orderPct) {
    var afterBundle = Math.floor(unitPriceCents * qty * (100 - tierPct) / 100);
    return lineFinalAfterOrder(afterBundle, orderPct);
  }

  /* Predict a line's LANDED price (cents) for the PDP ATC button: unit x
     qty with the active cart-level order code applied (floor-the-discount),
     matching the standard ghost row + what the cart actually charges.
     Standard-mode products aren't bundle-eligible, so no product/tier
     discount applies here; with no active code this returns full unit x qty. */
  function predictLineCents(unitCents, qty) {
    var u = parseInt(unitCents, 10) || 0;
    var q = parseInt(qty, 10) || 1;
    return lineFinalAfterOrder(u * q, readActiveOrderPct());
  }

  /* One collapsed, non-interactive bundle chip (qty + label). Ghost
     chips can't carry real line keys yet, so the face is disabled
     until the section swap brings the live, steppable chip. */
  function buildGhostChip(qty, label) {
    return (
      '<div class="hn-bundle-chip" role="listitem">' +
        '<button type="button" class="hn-bundle-chip__face hn-bundle-chip__face--collapsed" disabled aria-label="Adding to cart">' +
          '<span class="hn-bundle-chip__qty">' + qty + '&times;</span>' +
          '<span class="hn-bundle-chip__name">' + escapeHtml(label) + '</span>' +
        '</button>' +
      '</div>'
    );
  }

  /* Build the bundle ghost <tr>. group = [{ variant, qty }] (distinct
     variants, already merged). Renders the combined card when there
     are >=2 distinct variants, else the single-variant member card. */
  function buildBundleGhostHTML(group, productData, totalQty, tierPct, orderPct) {
    var isCombined = group.length >= 2;
    var hasOnlyDefault = !!productData.has_only_default_variant;

    var retailTotal = 0;
    var origTotal = 0;
    var effectiveTotal = 0; /* Σ per-line final (tier floored on price, order floored on discount). */
    var chipsHTML = '';
    for (var i = 0; i < group.length; i++) {
      var v = group[i].variant;
      var q = group[i].qty;
      var unit = parseInt(v.price, 10) || 0;
      var compareAt = parseInt(v.compare_at_price, 10) || 0;
      var retailUnit = compareAt > unit ? compareAt : unit;
      retailTotal += retailUnit * q;
      origTotal += unit * q;
      effectiveTotal += bundleLineFinalCents(unit, q, tierPct, orderPct);
      var label = (!isCombined && hasOnlyDefault)
        ? (q === 1 ? 'Item' : 'Items')
        : v.title;
      chipsHTML += buildGhostChip(q, label);
    }

    /* Media: first variant's image, else the product's featured image. */
    var firstImg = group[0].variant.featured_image || productData.featured_image;
    var imgHTML = '';
    if (firstImg) {
      imgHTML =
        '<a href="' + escapeHtml(productData.url) + '" class="cart-item__link" tabindex="-1" aria-hidden="true"></a>' +
        '<img class="cart-item__image" src="' + escapeHtml(firstImg) +
        '" alt="' + escapeHtml(productData.featured_image_alt || productData.title) +
        '" loading="lazy" width="150" height="150">';
    }

    /* Discounts: optional SALE markdown chip, the synthetic tier chip
       ("N Pack (X% OFF)"), then any active cart-level code chips.
       Order matches the Liquid render. */
    var saleHTML = '';
    var markdown = retailTotal - origTotal;
    if (markdown > 0 && retailTotal > 0) {
      var salePct = Math.round((markdown * 100) / retailTotal);
      saleHTML =
        '<li class="discounts__discount">' + DISCOUNT_ICON_SVG + ' SALE (' + salePct + '% OFF)</li>';
    }
    var tierHTML = '';
    if (tierPct > 0) {
      tierHTML =
        '<li class="discounts__discount">' + DISCOUNT_ICON_SVG + ' ' + totalQty + ' Pack (' + tierPct + '% OFF)</li>';
    }
    var discountsHTML =
      '<ul class="discounts list-unstyled" role="list" aria-label="Discount">' +
        saleHTML + tierHTML + snapshotCartLevelDiscountsHTML() +
      '</ul>';

    /* Price: strike retail + effective when they differ (a bundle
       always discounts, so this is the normal branch). */
    var priceBlock;
    if (retailTotal !== effectiveTotal) {
      priceBlock =
        '<div class="cart-item__price-wrapper">' +
          '<div class="cart-item__discounted-prices">' +
            '<s class="cart-item__old-price price price--end">' + formatMoneyCents(retailTotal, productData.money_format) + '</s>' +
            '<span class="price price--end">' + formatMoneyCents(effectiveTotal, productData.money_format) + '</span>' +
          '</div>' +
        '</div>';
    } else {
      priceBlock =
        '<div class="cart-item__price-wrapper">' +
          '<span class="price price--end">' + formatMoneyCents(retailTotal, productData.money_format) + '</span>' +
        '</div>';
    }

    var rowClass = isCombined ? 'cart-item cart-item--bundle-combined' : 'cart-item cart-item--bundle-member';
    var rowIdAttr = isCombined
      ? ' data-hn-bundle-product-id="' + escapeHtml(productData.id) + '"'
      : ' data-hn-variant-id="' + escapeHtml(group[0].variant.id) + '"';
    var removeAttr = isCombined ? 'data-hn-bundle-remove' : 'data-hn-bundle-variant-remove';

    var html = (
      '<tr class="' + rowClass + '" data-hn-pdp-ghost' + rowIdAttr + ' role="row">' +
        '<td class="cart-item__media" role="cell">' + imgHTML + '</td>' +
        '<td class="cart-item__details" role="cell">' +
          discountsHTML +
          '<a href="' + escapeHtml(productData.url) + '" class="cart-item__name h4 break">' + escapeHtml(productData.title) + '</a>' +
          '<div class="hn-bundle-chips" role="list">' + chipsHTML + '</div>' +
        '</td>' +
        '<td class="cart-item__totals right" role="cell">' +
          priceBlock +
          '<button type="button" class="cart-remove-button hn-bundle-member__remove-btn" ' + removeAttr + ' disabled tabindex="-1" aria-hidden="true"><span>Remove</span></button>' +
        '</td>' +
        '<td class="cart-item__quantity" role="cell"></td>' +
      '</tr>'
    );

    /* effectiveTotal (per-line final, post all discounts) is both the
       row's price and the footer's final delta — same number, so the
       row, Estimated Total, and Total Savings stay in lockstep. */
    return {
      html: html,
      retailTotal: retailTotal,
      effectiveTotal: effectiveTotal
    };
  }

  /* Insert a full-fidelity bundle ghost for a PDP bundle ATC.
     mergedItems = [{ id, quantity }] (already merged by variant).
     Returns the { row, revert } handle, or null when it can't build
     a faithful ghost — in which case the caller keeps the prior
     open-drawer-and-wait behavior:
       - product data / a variant can't be resolved
       - the product is ALREADY in the cart (server will regroup
         existing + new lines into one card; we can't predict that
         merge, so let the section swap reconcile)
       - total qty is below the grouping threshold with mixed
         variants (only possible on a non-default ladder) */
  function insertBundleGhost(mergedItems) {
    if (!mergedItems || !mergedItems.length) return null;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    var productData = readProductData();
    if (!productData || !productData.variants) return null;

    /* Resolve each line to a product-data variant. */
    var group = [];
    var totalQty = 0;
    for (var i = 0; i < mergedItems.length; i++) {
      var vid = parseInt(mergedItems[i].id, 10);
      var q = parseInt(mergedItems[i].quantity, 10) || 0;
      if (!vid || q <= 0) continue;
      var v = null;
      for (var j = 0; j < productData.variants.length; j++) {
        if (productData.variants[j].id === vid) { v = productData.variants[j]; break; }
      }
      if (!v) return null; /* unknown variant → can't build faithfully */
      group.push({ variant: v, qty: q });
      totalQty += q;
    }
    if (!group.length) return null;

    /* If this product already has any representation in the drawer,
       the server merges everything into one regrouped card. Predicting
       that merge optimistically is fragile, so bail and let the swap
       handle it (count + shipbar still bump in the caller). */
    if (drawer.querySelector('[data-hn-bundle-product-id="' + productData.id + '"]')) return null;
    for (var k = 0; k < productData.variants.length; k++) {
      if (drawer.querySelector('[data-hn-variant-id="' + productData.variants[k].id + '"]:not([data-hn-pdp-ghost])')) {
        return null;
      }
    }

    /* Min qty at which the drawer groups lines into a bundle card =
       the ladder's lowest tier, floored at 2 (mirrors the Liquid). */
    var tiers = getBundleTiers();
    var minTier = 2;
    if (tiers && tiers.qtys && tiers.qtys.length) {
      minTier = Math.max(2, tiers.qtys[0]);
    }

    /* Below the grouping threshold the server renders plain rows, not
       a bundle card. Single variant → reuse the single-row ghost; a
       sub-tier mix of variants (non-default ladder only) → skip. */
    /* orderPct (any active cart-level code) feeds both the footer
       delta and the bundle line math, so read it once. */
    var orderPct = readActiveOrderPct();

    if (totalQty < minTier) {
      if (group.length === 1) {
        var v0 = group[0].variant;
        var single = insertGhostRow({
          variant_id: v0.id,
          quantity: group[0].qty,
          title: productData.title,
          url: productData.url,
          image: v0.featured_image || productData.featured_image,
          image_alt: productData.featured_image_alt || productData.title,
          image_height: 150,
          price: v0.price,
          variant_title: v0.title,
          has_only_default_variant: productData.has_only_default_variant,
          money_format: productData.money_format
        });
        /* A below-tier single line is a plain row: no product discount,
           so its final = price x qty with the order code applied
           (floor-the-discount, via lineFinalAfterOrder). */
        if (single) {
          var u0 = parseInt(v0.price, 10) || 0;
          var c0 = parseInt(v0.compare_at_price, 10) || 0;
          single.footerFinalDelta = lineFinalAfterOrder(u0 * group[0].qty, orderPct);
          single.footerRetailDelta = (c0 > u0 ? c0 : u0) * group[0].qty;
        }
        return single;
      }
      return null;
    }

    var tierPct = bundleTierPctFor(totalQty);
    var built = buildBundleGhostHTML(group, productData, totalQty, tierPct, orderPct);
    var result = scaffoldAndPrependGhost(built.html);
    if (result) {
      result.footerFinalDelta = built.effectiveTotal;
      result.footerRetailDelta = built.retailTotal;
    }
    return result;
  }

  /* ---------- Optimistic cart-count badge ----------
     Header bubble structure (per cart-icon-bubble snippet):
       <a id="cart-icon-bubble">
         [svg icon]
         <span class="visually-hidden">...</span>
         {% if cart != empty %}
           <div class="cart-count-bubble">
             {% if cart.item_count < 100 %}
               <span aria-hidden="true">{{ count }}</span>
             {% endif %}
             <span class="visually-hidden">...</span>
           </div>
         {% endif %}
       </a>

     Three states to handle when bumping:
       - Badge present + visible count span: increment
       - Badge present but no visible span (count >= 100): no-op
         the visible number, let server reconcile
       - No badge at all (cart was empty): create one with our
         delta, tagged so revert can cleanly remove it

     Returns the delta actually applied (0 if no-op) so caller
     can track for rollback. */

  function bumpCountBadge(delta) {
    delta = parseInt(delta, 10) || 0;
    if (!delta) return 0;
    bumpDrawerHeading(delta);
    var bubble = document.querySelector('#cart-icon-bubble');
    if (!bubble) return 0;

    var existing = bubble.querySelector('.cart-count-bubble');
    if (!existing) {
      /* No badge present. Only build one if we're incrementing —
         decrementing from an empty cart is meaningless. */
      if (delta < 0) return 0;
      /* Build fresh badge matching the snippet structure. Tag with
         data-attr so revert can detect "we created this". */
      var fresh = document.createElement('div');
      fresh.className = 'cart-count-bubble';
      fresh.setAttribute('data-hn-optimistic-bubble', '');
      fresh.innerHTML =
        '<span aria-hidden="true">' + delta + '</span>' +
        '<span class="visually-hidden">' + delta + ' item' +
        (delta === 1 ? '' : 's') + ' in cart</span>';
      bubble.appendChild(fresh);
      return delta;
    }

    var span = existing.querySelector('[aria-hidden="true"]');
    if (!span) {
      /* Count was already >= 100 (visible span hidden per the
         snippet). We can't know the exact current value, so just
         leave the display alone — the section swap will reconcile
         when it arrives. */
      return delta;
    }

    var current = parseInt(span.textContent, 10) || 0;
    var next = current + delta;
    if (next <= 0) {
      /* Cart back to empty — remove the badge entirely so the
         cart icon renders the empty-state look. */
      existing.parentNode.removeChild(existing);
    } else if (next >= 100) {
      /* Crossed 100 going up: snippet hides the visible number. */
      span.parentNode.removeChild(span);
    } else {
      span.textContent = String(next);
    }
    return delta;
  }

  function revertCountBadge(delta) {
    delta = parseInt(delta, 10) || 0;
    if (!delta) return;
    /* Apply the inverse delta via the same bump function. Handles
       signed deltas correctly: positive bumps revert by subtracting,
       negative bumps revert by adding back. */
    bumpCountBadge(-delta);
  }

  /* ---------- Drawer heading ("Your Cart 2 Items") ----------
     Cart drawer's own heading is rendered server-side from
     cart.item_count, so it's stale right after an optimistic
     add until the section swap. Update it client-side in
     lockstep with the header badge so both reflect the new
     count on the same frame as the click. */

  function bumpDrawerHeading(delta) {
    delta = parseInt(delta, 10) || 0;
    if (!delta) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var heading = drawer.querySelector('.drawer__heading');
    if (!heading) return;

    /* Stash the original text exactly once so a full rollback
       (back to 0 items) can restore whatever empty-state copy
       the merchant has configured (translatable + customizable). */
    if (!heading.hasAttribute('data-hn-original-heading')) {
      heading.setAttribute('data-hn-original-heading', heading.textContent.trim());
    }

    var current = parseHeadingCount(heading);
    var next = current + delta;
    if (next <= 0) {
      /* Back to 0 — restore original empty-state heading. */
      var original = heading.getAttribute('data-hn-original-heading');
      if (original) heading.textContent = original;
      heading.removeAttribute('data-hn-original-heading');
    } else {
      heading.textContent = next + ' ' + (next === 1 ? 'Item' : 'Items');
    }
  }

  function revertDrawerHeading(delta) {
    delta = parseInt(delta, 10) || 0;
    if (!delta) return;
    bumpDrawerHeading(-delta);
  }

  function parseHeadingCount(heading) {
    var match = heading.textContent.trim().match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }

  /* ---------- Free-shipping progress bar ----------
     Recalculates the bar's message, fill width, indicator
     position, and unlocked state from the current subtotal
     (stashed as data-attr on the bar) + the price delta of
     the add. Server section swap reconciles on response.

     Bar is gated by `cart != empty` in the snippet — for
     empty-cart-first-add the bar doesn't exist yet, so we
     no-op and let the server section swap render it.

     SVG strings inlined to match the snippet exactly so the
     swap from truck (in-progress) to check (unlocked) is
     visually pixel-identical to the server-rendered version. */

  var SHIPBAR_TRUCK_SVG =
    '<svg viewBox="0 0 24 24" fill="currentColor">' +
      '<path d="M3 6h11v9H3z"/>' +
      '<path d="M14 9h4l3 3v3h-7zM6.5 16a2 2 0 100 4 2 2 0 000-4zm11 0a2 2 0 100 4 2 2 0 000-4z"/>' +
    '</svg>';
  var SHIPBAR_CHECK_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M5 12l5 5L20 7"/>' +
    '</svg>';

  function applyShipbarMath(bar, newSubtotal) {
    var threshold = parseInt(bar.getAttribute('data-hn-freeship-threshold'), 10) || 0;
    if (!threshold) return;

    var remaining = threshold - newSubtotal;
    var pct = Math.min(100, Math.max(0, Math.round((newSubtotal * 100) / threshold)));
    var truckPos = Math.min(96, Math.max(4, pct));
    var unlocked = remaining <= 0;

    /* Message: matches snippet copy exactly. */
    var text = bar.querySelector('.hn-cart-shipbar__text');
    if (text) {
      if (unlocked) {
        text.innerHTML = 'You unlocked <em>FREE</em> shipping!';
      } else {
        text.innerHTML =
          'Add <strong>' + formatMoneyCents(remaining) +
          '</strong> more for <em>FREE</em> shipping!';
      }
    }

    /* Fill width */
    var fill = bar.querySelector('.hn-cart-shipbar__fill');
    if (fill) fill.style.width = pct + '%';

    /* Truck indicator: position + swap SVG on unlock crossing */
    var indicator = bar.querySelector('.hn-cart-shipbar__indicator');
    if (indicator) {
      indicator.style.left = (unlocked ? 96 : truckPos) + '%';
      var expectedSvg = unlocked ? SHIPBAR_CHECK_SVG : SHIPBAR_TRUCK_SVG;
      /* Only re-write innerHTML when the SVG type actually
         changes (cheaper + avoids flicker on repeat updates). */
      var hasCheck = indicator.querySelector('path[d="M5 12l5 5L20 7"]') !== null;
      if (unlocked !== hasCheck) {
        indicator.innerHTML = expectedSvg;
      }
    }

    /* ARIA progressbar */
    var track = bar.querySelector('.hn-cart-shipbar__track');
    if (track) track.setAttribute('aria-valuenow', String(pct));

    bar.classList.toggle('hn-cart-shipbar--unlocked', unlocked);
    bar.setAttribute('data-hn-cart-subtotal', String(newSubtotal));
  }

  function bumpShippingBar(deltaCents) {
    var delta = parseInt(deltaCents, 10) || 0;
    if (!delta) return 0;
    var bar = document.querySelector('.hn-cart-shipbar');
    if (!bar) return 0;
    var current = parseInt(bar.getAttribute('data-hn-cart-subtotal'), 10) || 0;
    var next = current + delta;
    if (next < 0) next = 0;
    applyShipbarMath(bar, next);
    return delta;
  }

  function revertShippingBar(deltaCents) {
    var delta = parseInt(deltaCents, 10) || 0;
    if (!delta) return;
    bumpShippingBar(-delta);
  }

  /* ---------- Main product form: full pattern ---------- */

  /* Tracks the most recent optimistic state per form so the
     pubsub event handlers can clean up the right one. Keyed by
     a per-submit token rather than the form so rapid resubmits
     don't tangle each other's revert state. */
  var pendingByToken = {};
  var nextToken = 1;

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches('[data-hn-pdp-product-form], [data-hn-optimistic-product-form]')) return;
    /* DO NOT preventDefault here. Dawn's <product-form> custom
       element has its own submit listener that does the actual
       AJAX. We're layering optimistic UI on top, not replacing. */

    /* Double-submit guard. Dawn dedupes its own AJAX via
       aria-disabled, but the submit EVENT still fires for every
       click — so without this we'd insert N ghost rows on rapid
       double-taps even though only one add actually happens.
       Cleared on the next cartUpdate / cartError / safety
       timeout via _hnPdpPending below. */
    if (form.__hnPdpPending) return;
    form.__hnPdpPending = true;

    var productData = readProductData(form);
    var variant = resolveVariant(form, productData);
    if (!productData || !variant) {
      form.__hnPdpPending = false;
      return; /* Bail silently; Dawn will still add */
    }

    /* Open drawer + insert ghost immediately. */
    if (window.HnAnalytics && typeof window.HnAnalytics.setNextCartDrawerOpenReason === 'function') {
      window.HnAnalytics.setNextCartDrawerOpenReason(
        form.matches('[data-hn-pdp-product-form]') ? 'pdp_atc' : 'collection_quick_add'
      );
    }
    openDrawerOptimistic(form);

    /* The qty stepper sits OUTSIDE the <form> and is wired to it via the
       form="" attribute, so form.querySelector (descendants only) misses
       it. form.elements includes form-ASSOCIATED controls, so fall back
       to that — otherwise the ghost would show qty 1 for a qty>1 add. */
    var qtyInput = form.querySelector('input[name="quantity"]') ||
      (form.elements && form.elements.namedItem ? form.elements.namedItem('quantity') : null);
    var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    var ctx = {
      variant_id: variant.id,
      quantity: qty,
      title: productData.title,
      url: productData.url,
      image: variant.featured_image || productData.featured_image,
      image_alt: productData.featured_image_alt || productData.title,
      image_height: 150,
      price: variant.price,
      variant_title: variant.title,
      has_only_default_variant: productData.has_only_default_variant,
      money_format: productData.money_format
    };

    var insert = insertGhostRow(ctx);
    if (!insert) {
      form.__hnPdpPending = false;
      return;
    }

    /* Bump the header cart-count badge so it reflects the add
       in the same frame as the click. Server section swap will
       overwrite this with the canonical count on response. */
    var countDelta = bumpCountBadge(qty);

    /* Bump the free-shipping bar by the line-add total cents
       (variant price * quantity). Captured for rollback. */
    var unitPrice = parseInt(variant.price, 10) || 0;
    var shipDelta = bumpShippingBar(unitPrice * qty);

    /* Footer Estimated Total + Total Savings. A product on this form is
       non-bundle (bundle products use the bundle ATC), so its final =
       price x qty with the order code applied (floor-the-discount via
       lineFinalAfterOrder) — the SAME helper + inputs buildGhostRowHTML
       used for the row, so the row and footer agree by construction.
       Reverts on cartError. */
    var compareAtCents = parseInt(variant.compare_at_price, 10) || 0;
    var footerFinalDelta = lineFinalAfterOrder(unitPrice * qty, readActiveOrderPct());
    var footerRetailDelta = Math.max(unitPrice, compareAtCents) * qty;
    var footerRevert = null;
    if (window.HnCart && typeof window.HnCart.applyLineAddToFooter === 'function') {
      footerRevert = window.HnCart.applyLineAddToFooter(footerFinalDelta, footerRetailDelta);
    }

    var token = nextToken++;
    pendingByToken[token] = {
      insert: insert,
      form: form,
      countDelta: countDelta,
      shipDelta: shipDelta,
      footerRevert: footerRevert
    };
  });

  /* Cleanup hooks: Dawn fires cartUpdate after success, cartError
     after failure. cartUpdate → ghost gets replaced anyway by
     Dawn's section swap (which replaces #CartDrawer.innerHTML),
     but we explicitly drop the loading state. cartError →
     revert the ghost and close drawer so user sees the original
     state restored. */
  function clearAllPending() {
    var tokens = Object.keys(pendingByToken);
    for (var i = 0; i < tokens.length; i++) {
      var entry = pendingByToken[tokens[i]];
      if (entry && entry.form) entry.form.__hnPdpPending = false;
    }
    pendingByToken = {};
  }

  function onAnyCartUpdate() {
    clearDrawerLoading();
    /* Drop refs to pending optimistic states. The section swap
       that Dawn does replaces the entire #CartDrawer body so
       ghost rows are already gone from the DOM — but we still
       need to release the double-submit guard on the form. */
    clearAllPending();
  }

  function onAnyCartError() {
    var tokens = Object.keys(pendingByToken);
    for (var i = 0; i < tokens.length; i++) {
      var entry = pendingByToken[tokens[i]];
      if (!entry) continue;
      if (entry.insert) revertGhostRow(entry.insert.row, entry.insert.revert);
      if (entry.countDelta) revertCountBadge(entry.countDelta);
      if (entry.shipDelta) revertShippingBar(entry.shipDelta);
      if (entry.footerRevert && window.HnCart && typeof window.HnCart.revertLineAddToFooter === 'function') {
        window.HnCart.revertLineAddToFooter(entry.footerRevert);
      }
      if (entry.form) entry.form.__hnPdpPending = false;
    }
    pendingByToken = {};
    clearDrawerLoading();
  }

  /* pubsub.js is loaded with `defer`, so `window.subscribe`
     and the event map may not be defined yet at the
     parse-time tick where this IIFE runs. Wait until DOM ready
     to subscribe; by then both globals exist.

     Fallback: also poll a few times on a short interval, in
     case pubsub is loaded even later (some theme configs).
     Stops polling once subscribed. */
  function getPubSubEvents() {
    if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS) return PUB_SUB_EVENTS;
    if (window.PUB_SUB_EVENTS) return window.PUB_SUB_EVENTS;
    return null;
  }

  function bindPubSub() {
    if (bindPubSub.__done) return true;
    var events = getPubSubEvents();
    if (typeof window.subscribe !== 'function' || !events) {
      return false;
    }
    if (events.cartUpdate) {
      window.subscribe(events.cartUpdate, onAnyCartUpdate);
    }
    if (events.cartError) {
      window.subscribe(events.cartError, onAnyCartError);
    }
    bindPubSub.__done = true;
    return true;
  }

  function startPubSubBindAttempts() {
    if (bindPubSub()) return;
    /* Poll for up to 5 seconds at 100ms intervals (50 tries).
       After that, give up — the safety timeout below catches
       the worst case (stuck loading bar) anyway. */
    var tries = 0;
    var interval = setInterval(function () {
      if (bindPubSub() || ++tries > 50) {
        clearInterval(interval);
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startPubSubBindAttempts);
  } else {
    startPubSubBindAttempts();
  }

  /* Custom event our hn-cart-drawer.js dispatches — bound
     unconditionally since it doesn't depend on pubsub.js */
  document.addEventListener('hn:cart:updated', onAnyCartUpdate);

  /* Safety net: if NO event fires within 4 seconds of opening
     the drawer in loading state (custom theme that overrides
     publish, network hang, etc.), force-clear the loading
     class so the bar can't get stuck forever. Re-armed every
     time the drawer is opened optimistically. */
  var safetyTimeout = null;
  function armSafetyTimeout() {
    if (safetyTimeout) clearTimeout(safetyTimeout);
    safetyTimeout = setTimeout(function () {
      /* Also flush the pending map so the double-submit guard
         doesn't get permanently stuck if both cartUpdate and
         cartError silently fail to fire. Re-enable checkout
         buttons so the customer isn't locked out forever if
         the network hung. */
      clearAllPending();
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.remove('is-loading');
      enableCheckoutButtons();
      safetyTimeout = null;
    }, 4000);
  }
  function clearSafetyTimeout() {
    if (safetyTimeout) {
      clearTimeout(safetyTimeout);
      safetyTimeout = null;
    }
  }

  /* ---------- Public API ---------- */

  window.HnPdpCart = {
    __bound: true,
    openDrawerOptimistic: openDrawerOptimistic,
    clearDrawerLoading: clearDrawerLoading,
    insertGhostRow: insertGhostRow,
    insertBundleGhost: insertBundleGhost,
    revertGhostRow: revertGhostRow,
    bumpCountBadge: bumpCountBadge,
    revertCountBadge: revertCountBadge,
    bumpShippingBar: bumpShippingBar,
    revertShippingBar: revertShippingBar,
    readProductData: readProductData,
    resolveVariant: resolveVariant,
    buildGhostRowHTML: buildGhostRowHTML,
    predictLineCents: predictLineCents,
    readActiveOrderPct: readActiveOrderPct,
    lineFinalAfterOrder: lineFinalAfterOrder,
    formatMoneyCents: formatMoneyCents,
    escapeHtml: escapeHtml
  };
})();
